/**
 * Slack-specific message types and schemas
 */

import { z } from "zod";
import { MessageBaseSchema } from "../message";

// ============================================================================
// SLACK MESSAGE METADATA SCHEMAS
// ============================================================================

export const SlackMessageMetadataSchema = z.object({
  threadId: z.string().optional(),
  threadTs: z.string().optional(),
  reactions: z.record(z.string(), z.number()).optional(),
  botId: z.string().optional(),
  botName: z.string().optional(),
  isBot: z.boolean().optional(),
  isAppMessage: z.boolean().optional(),
  edited: z
    .object({
      user: z.string(),
      ts: z.string(),
    })
    .optional(),
  files: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        url: z.string().url(),
        size: z.number().optional(),
        mimetype: z.string().optional(),
      })
    )
    .optional(),
}).strict();

export type SlackMessageMetadata = z.infer<typeof SlackMessageMetadataSchema>;

// ============================================================================
// SLACK MESSAGE SCHEMAS
// ============================================================================

export const SlackMessageSchema = MessageBaseSchema.extend({
  provider: z.literal("slack-oauth"),
  channel: z.object({
    id: z.string(),
    name: z.string(),
  }).strict(),
  user: z.object({
    id: z.string(),
    name: z.string(),
    avatar: z.string().optional(),
  }).strict(),
  timestamp: z.string(),
  threadTs: z.string().optional(),
  replies: z.number().int().nonnegative().optional(),
  metadata: SlackMessageMetadataSchema,
}).strict();

export type SlackMessage = z.infer<typeof SlackMessageSchema>;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Type guard to check if a message is a Slack message
 */
export function isSlackMessage(message: any): message is SlackMessage {
  return message?.provider === "slack-oauth";
}

/**
 * Validate a Slack message
 */
export function validateSlackMessage(data: unknown): SlackMessage {
  return SlackMessageSchema.parse(data);
}
